import * as undoActions from 'owa-mail-undo';
import composeStore from 'owa-mail-compose-store/lib/store/composeStore';
import copyItemsService from 'owa-mail-triage-action/lib/services/copyItemsService';
import createItemContextsForDeleteStoreUpdate from './helpers/createItemContextsForDeleteStoreUpdate';
import { getSortColumnToLog } from './datapoints';
import DeleteItemResponseMessage from 'owa-service/lib/contract/DeleteItemResponseMessage';
import deleteItemService from 'owa-mail-store/lib/services/deleteItemService';
import deleteItemsStoreUpdate from 'owa-mail-actions/lib/triage/deleteItemsStoreUpdate';
import deleteMailListRowsHelper from './helpers/deleteMailListRowsHelper';
import DisposalType from 'owa-service/lib/contract/DisposalType';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import getServiceContextFolderId from 'owa-mail-store/lib/services/utils/getServiceContextFolderId';
import handleBlockActionForShadowMailbox from '../actions/helpers/handleBlockActionForShadowMailbox';
import Item from 'owa-service/lib/contract/Item';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import restoreItemService from '../services/restoreItemService';
import showNotificationOnDelete from './helpers/showNotificationOnDelete';
import strings from 'owa-strings/owa-mail-strings';
import { action, select } from 'satcheljs/lib/legacy';
import { InstrumentationContext, isDeletedItemsFolderFromAnyMailbox } from 'owa-mail-store';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import { doesTableSupportUndo } from 'owa-mail-undo';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import {
    getRowIdsFromRowKeys,
    getRowKeysFromRowIds,
    getSelectedTableView,
    TableView,
} from 'owa-mail-list-store';
import { getStore } from 'owa-mail-store/lib/store/Store';
import { MailboxInfo, MailboxType } from 'owa-client-ids';
import { resetFocus } from 'owa-mail-focus-manager';
import { returnTopExecutingActionDatapoint, logUsage } from 'owa-analytics';
import { userMailInteractionAction } from 'owa-mail-actions';
import {
    isPublicFolder,
    SHARED_FOLDERS_TREE_TYPE,
    isFolderOfTreeType,
    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
} from 'owa-folders';
import RequestOptions from 'owa-service/lib/RequestOptions';
import shouldPerformRowRemovalInstantUpdate from './helpers/shouldPerformRowRemovalInstantUpdate';
import {
    onTriageActionCompleted,
    onTriageActionFailed,
} from 'owa-mail-triage-action-response-processor';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { handleServerResponseSuccessAndError } from 'owa-mail-triage-action-response-processor';

export interface DeleteItemsFromTableState {
    originalTableView: TableView;
}

export interface DeleteItemFromReadingPaneState {
    item: Item;
}

function isReplyingToOneOfItems(itemIds: string[]): boolean {
    const replyItemId =
        composeStore.primaryComposeId &&
        composeStore.viewStates.get(composeStore.primaryComposeId).itemId;
    return replyItemId && itemIds.indexOf(replyItemId.Id) >= 0;
}

/**
 * The action worker which handles deleting the given items
 * Note: This aciton is exported only for unit testing purposes
 * @param itemIds - the conversation that need to be deleted
 * @param originalTableView - the table view the conversation should be deleted from
 * @param disposalType - the disposal type
 * @param parentFolderId - the parent FolderId
 * @param actionSource - the action source
 */
export let deleteItemsWithoutConfirmDialog = createLegacyActionWithDatapoint(
    'deleteItemsWithoutConfirmDialog',
    {
        name: 'TnS_DeleteItem',
        options: { isQos: true },
        customData: (
            itemIds: string[],
            disposalType: DisposalType,
            parentFolderId: string,
            actionSource: ActionSource,
            instrumentationContexts: InstrumentationContext[],
            requestInitOptions?: RequestOptions,
            allowUndoAction?: boolean
        ) => [itemIds.length, disposalType],
        cosmosOnlyData: (
            itemIds: string[],
            disposalType: DisposalType,
            parentFolderId: string,
            actionSource: ActionSource,
            instrumentationContexts: InstrumentationContext[],
            requestInitOptions?: RequestOptions,
            allowUndoAction?: boolean
        ) => JSON.stringify({ itemIds: itemIds }),
        actionSource: (
            itemIds: string[],
            disposalType: DisposalType,
            parentFolderId: string,
            actionSource: ActionSource,
            instrumentationContexts: InstrumentationContext[],
            requestInitOptions?: RequestOptions,
            allowUndoAction?: boolean
        ) => actionSource,
    }
)(function deleteItemsWithoutConfirmDialog(
    itemIds: string[],
    disposalType: DisposalType,
    parentFolderId: string,
    actionSource: ActionSource,
    instrumentationContexts: InstrumentationContext[],
    requestInitOptions?: RequestOptions,
    allowUndoAction?: boolean
): Promise<void> {
    const itemContexts = createItemContextsForDeleteStoreUpdate(itemIds);

    const rowIds: string[] = itemContexts.map(itemContext => {
        return itemContext.itemId;
    });

    // VSO: 45716 - [Refactor] Remove usage of geSelectedTableView from delete item
    const tableView = getSelectedTableView();
    let serverFolderId = null;
    var rowKeys = [];

    // When tableView exists, perform instant update
    if (tableView) {
        rowKeys = getRowKeysFromRowIds(rowIds, tableView);
        serverFolderId = tableView.serverFolderId;
        // Perform instant update
        if (
            shouldPerformRowRemovalInstantUpdate(
                tableView.tableQuery,
                true /* isRowMovingToDeletedItemsFolder */
            )
        ) {
            deleteItemsStoreUpdate(itemContexts, parentFolderId);
        }
    }

    undoActions.clearLastUndoableAction();

    // Make service request
    const serviceItemIds = itemIds.map(itemIdValue => {
        return {
            Id: itemIdValue,
        };
    });

    const deleteItemsPromise = deleteItemService(serviceItemIds, disposalType, requestInitOptions);

    undoActions.addActionToUndoStack(async () => {
        // Wait for delete promise and make restore call after
        const responseItems = await deleteItemsPromise;
        if (allowUndoAction) {
            const movedItemIds = [];
            for (const responseItem of responseItems) {
                const deleteItemResponse = <DeleteItemResponseMessage>responseItem;
                if (deleteItemResponse.ResponseClass == 'Success') {
                    movedItemIds.push(deleteItemResponse.MovedItemId.Id);
                }
            }

            if (movedItemIds.length > 0) {
                return restoreItemService(movedItemIds);
            }
        }
        return Promise.resolve();
    }, serverFolderId);

    // Only show notifications for user initiated actions. E.g. block sender calls into
    // deleteItems which would trigger the delete notification without this check.
    if (actionSource) {
        showNotificationOnDelete(itemIds.length, allowUndoAction);
    }

    userMailInteractionAction('Delete', instrumentationContexts);

    returnTopExecutingActionDatapoint().markUserPerceivedTime();

    return deleteItemsPromise
        .then(response => {
            onTriageActionCompleted(
                rowKeys,
                parentFolderId,
                !!tableView /* shouldRemoveRowsFromTombstone */
            );

            return handleServerResponseSuccessAndError(response);
        })
        .catch(error => {
            onTriageActionCompleted(
                rowKeys,
                parentFolderId,
                !!tableView /* shouldRemoveRowsFromTombstone */
            );
            onTriageActionFailed(tableView);
            return Promise.reject(error);
        });
});

/**
 * Deletes selected items
 * @param itemIds - items ids to be deleted
 * @param disposalType - disposal type for the items to be deleted
 * @param actionSource - action source of the delete action
 * @param instrumentationContexts - instrumentation context of the items to be deleted
 * @param mailboxinfo - mailbox info of the table to which the rows belong
 * @param disableResetFocus - an optional param to disable call resetFocus() after delete
 */
export let deleteItems = action('deleteItems')(function deleteItems(
    itemIds: string[],
    disposalType: DisposalType,
    instrumentationContexts: InstrumentationContext[],
    actionSource?: ActionSource,
    mailboxInfo?: MailboxInfo,
    disableResetFocus?: boolean
): Promise<void> {
    // Early return if the action is blocked for shadow mailbox
    const firstItem = getStore().items.get(itemIds[0]);
    const sourceFolderId = firstItem.ParentFolderId.Id;
    if (handleBlockActionForShadowMailbox(sourceFolderId, null)) {
        return Promise.resolve();
    }

    const isSharedFolder: boolean = isFolderOfTreeType(sourceFolderId, SHARED_FOLDERS_TREE_TYPE);

    const requestInitOptions = mailboxInfo ? mailboxInfo.requestOptions : null;
    const isGroup = mailboxInfo && mailboxInfo.type == MailboxType.GroupMailbox;
    const isDeletedItemsFolder = isDeletedItemsFolderFromAnyMailbox(sourceFolderId);
    const shouldUserConfirmDeletion =
        (disposalType == 'SoftDelete' && isDeletedItemsFolder) ||
        isGroup ||
        isPublicFolder(sourceFolderId);

    return confirm(
        isGroup ? strings.confirmDeletingGroupItemTitle : '',
        isGroup ? strings.confirmDeletingGroupItem : strings.confirmDeletingItem,
        !shouldUserConfirmDeletion /*resolveImmediately*/,
        {
            // Wait until dialog is dismissed to avoid focus interruption
            delayCallbackAfterAnimation: true,
        }
    ).then((response: DialogResponse) => {
        // Not applicable for groups and archive mailbox folder
        const allowUndoAction =
            doesTableSupportUndo(sourceFolderId, mailboxInfo) &&
            disposalType === 'MoveToDeletedItems';
        if (response == DialogResponse.ok) {
            // For shared folder, delete item action is performed by first copying the item to deletedItems folder
            // And then item is deleted from the source folder.
            if (isSharedFolder) {
                const destinationFolderIdForSharedFolder = folderNameToId(
                    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID
                );
                return copyItemsService(
                    itemIds,
                    getServiceContextFolderId(destinationFolderIdForSharedFolder),
                    true /* RemoteExecute */
                ).then(() => {
                    return deleteItemsWithoutConfirmDialog(
                        itemIds,
                        'HardDelete' /* disposalType */,
                        sourceFolderId,
                        actionSource,
                        instrumentationContexts,
                        requestInitOptions,
                        allowUndoAction
                    );
                });
            } else {
                return deleteItemsWithoutConfirmDialog(
                    itemIds,
                    disposalType,
                    sourceFolderId,
                    actionSource,
                    instrumentationContexts,
                    requestInitOptions,
                    allowUndoAction
                );
            }
            if (!disableResetFocus) {
                resetFocus();
            }
        }

        return Promise.resolve();
    });
});

/**
 * Initiates deleting of selected items
 * @param itemRowKeys - row keys to be deleted
 * @param originalTableViewId - the table view the conversation should be deleted from
 * @param isExplicitSoftDelete - flag to indicate whether disposal type is soft delete
 * @param instrumentationContexts - instrumentation context of the items to be deleted
 * @param actionSource - the action source
 * @param state - some additional state required to complete the action
 */
export let deleteItemsFromTable = select<DeleteItemsFromTableState>({
    originalTableView: (conversationId: string, originalTableViewId: string) =>
        listViewStore.tableViews.get(originalTableViewId),
})(function deleteItemsFromTable(
    itemRowKeys: string[],
    originalTableViewId: string,
    isExplicitSoftDelete: boolean,
    instrumentationContexts: InstrumentationContext[],
    actionSource?: ActionSource,
    state?: DeleteItemsFromTableState
) {
    const confirmDialogStrings = {
        deleteNumberOfRowsTitle: strings.deleteNumberOfItems,
        deleteOneRowTitle: strings.deleteItem,
        confirmDeleteRows: strings.confirmDeletingItems,
    };

    logUsage('TnS_DeleteItemsFromTable', [
        actionSource,
        itemRowKeys.length,
        getSortColumnToLog(originalTableViewId),
    ]);

    return deleteMailListRowsHelper(
        itemRowKeys,
        originalTableViewId,
        isExplicitSoftDelete,
        (rowKeys: string[], disposalType: DisposalType) => {
            const rowIdStrings = getRowIdsFromRowKeys(rowKeys, originalTableViewId);
            return deleteItemsWithoutConfirmDialog(
                rowIdStrings,
                disposalType,
                state.originalTableView.tableQuery.folderId,
                actionSource,
                instrumentationContexts,
                getMailboxInfo(state.originalTableView).requestOptions,
                doesTableSupportUndo(
                    state.originalTableView.tableQuery.folderId,
                    getMailboxInfo(state.originalTableView)
                ) && disposalType == 'MoveToDeletedItems'
            );
        },
        isReplyingToOneOfItems,
        confirmDialogStrings,
        actionSource
    );
});
